<template>
  <v-container fluid class="pa-6">
    <v-row class="align-center mb-2" style="gap: 12px;">
      <v-col cols="auto">
        <v-card-title class="pa-0">Bulk Action</v-card-title>
      </v-col>
      <v-spacer />
      <v-col cols="auto" style="min-width: 240px;">
        <v-autocomplete
          v-model="entityIds"
          :items="projectItems"
          label="Projects (all if empty)"
          multiple
          chips
          small-chips
          deletable-chips
          clearable
          dense
          outlined
          hide-details
          @change="reload"
        />
      </v-col>
      <v-col cols="auto" style="min-width: 180px;">
        <v-select
          v-model="typeId"
          :items="contentTypes"
          item-text="name"
          item-value="type_id"
          label="Type *"
          dense
          outlined
          hide-details
          @change="reload"
        />
      </v-col>
      <v-col cols="auto" style="min-width: 200px;">
        <v-text-field
          v-model="search"
          label="Search name/slug"
          prepend-inner-icon="mdi-magnify"
          clearable
          dense
          outlined
          hide-details
        />
      </v-col>
      <v-col cols="auto" style="min-width: 150px;">
        <v-select
          v-model="status"
          :items="statusOptions"
          label="Status"
          clearable
          dense
          outlined
          hide-details
          @change="reload"
        />
      </v-col>
    </v-row>

    <v-card outlined class="rounded-lg">
      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="items"
        :loading="isLoading"
        :options.sync="pagination"
        :server-items-length="total"
        :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
        item-key="item_id"
        show-select
        fixed-header
        :height="tableHeight"
      >
        <template #top>
          <v-toolbar v-if="selected.length" flat dense color="blue-grey lighten-5">
            <span class="subtitle-2">{{ selected.length }} item(s) selected</span>
            <v-spacer />
            <v-btn small color="primary" class="mr-2" @click="statusDialog = true">Set status</v-btn>
            <v-btn small color="error" :loading="isBulking" @click="onBulkDelete">Delete</v-btn>
          </v-toolbar>
        </template>
        <template #item.entity_id="{ value }">
          {{ projectName(value) }}
        </template>
        <template #item.type_id="{ value }">
          {{ typeNameById[value] || value }}
        </template>
        <template #item.status="{ value }">
          <v-chip :color="statusColor[value] || 'grey'" small outlined>{{ value }}</v-chip>
        </template>
        <template #item.is_changed="{ value }">
          <v-icon v-if="value" small color="warning">mdi-alert-circle</v-icon>
        </template>
        <template #no-data>
          <span class="grey--text">{{ typeId ? 'No items' : 'Select a type to load items' }}</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- Set status dialog -->
    <v-dialog v-model="statusDialog" max-width="420">
      <v-card>
        <v-card-title>Set status for {{ selected.length }} item(s)</v-card-title>
        <v-card-text>
          <v-select
            v-model="bulkStatus"
            :items="statusOptions"
            label="New status"
            dense
            outlined
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="statusDialog = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!bulkStatus" :loading="isBulking" @click="onBulkStatus">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { API } from '@/utils/api'
import { STATUS_OPTIONS } from '@/components/cms/constants'

export default {
  data() {
    return {
      entityIds: [],
      typeId: null,
      search: '',
      status: null,
      types: [],
      items: [],
      total: 0,
      selected: [],
      pagination: {},
      isLoading: false,
      isBulking: false,
      statusDialog: false,
      bulkStatus: null,
      statusOptions: STATUS_OPTIONS,
      statusColor: {
        draft: 'orange',
        published: 'primary',
        archived: 'grey',
      },
      headers: [
        { text: 'Project', value: 'entity_id', width: 160 },
        { text: 'Type', value: 'type_id', width: 140 },
        { text: 'Name', value: 'name' },
        { text: 'Slug', value: 'slug' },
        { text: 'Status', value: 'status', width: 120 },
        { text: 'Changed', value: 'is_changed', width: 90 },
      ],
      searchTimer: null,
    }
  },
  computed: {
    projectItems() {
      return (this.$store.state.auth.entities || []).map(e => ({ text: e.name, value: e.entity_id }))
    },
    allEntityIds() {
      return (this.$store.state.auth.entities || []).map(e => e.entity_id)
    },
    contentTypes() {
      return this.types.filter(t => t.kind !== 'fragment')
    },
    typeNameById() {
      const map = {}
      for (const t of this.types) {
        map[t.type_id] = t.name
      }
      return map
    },
    tableHeight() {
      return this.selected.length ? 'calc(100vh - 290px)' : 'calc(100vh - 241px)'
    },
  },
  watch: {
    search() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      this.searchTimer = setTimeout(() => this.reload(), 300)
    },
    pagination: {
      handler() {
        if (this.typeId) {
          this.loadData()
        }
      },
      deep: true,
    },
  },
  created() {
    this.loadTypes()
  },
  methods: {
    loadTypes() {
      API.call('/cms/type/list.json', {}).then(r => {
        this.types = (r.data && r.data.items) || []
      }).catch(() => {
        this.types = []
      })
    },
    projectName(entityId) {
      const e = (this.$store.state.auth.entities || []).find(x => x.entity_id === entityId)
      return e ? e.name : entityId
    },
    reload() {
      if (this.pagination.page !== 1) {
        this.pagination = { ...this.pagination, page: 1 }
      } else {
        this.loadData()
      }
    },
    loadData() {
      if (!this.typeId) {
        this.items = []
        this.total = 0
        return
      }
      this.isLoading = true
      this.selected = []
      API.call('/cms/item/list.json', {
        entity_ids: this.entityIds.length ? this.entityIds : this.allEntityIds,
        type_id: this.typeId,
        search: this.search || undefined,
        status: this.status || undefined,
        page: this.pagination.page,
        perpage: this.pagination.itemsPerPage,
      }).then(r => {
        const data = r.data || {}
        this.items = data.items || []
        this.total = data.pagination ? data.pagination.total : this.items.length
      }).catch(() => {
        this.items = []
        this.total = 0
      }).finally(() => {
        this.isLoading = false
      })
    },
    selectedScope() {
      return {
        item_ids: this.selected.map(i => i.item_id),
        entity_ids: [...new Set(this.selected.map(i => i.entity_id))],
      }
    },
    reportBulk(r) {
      const data = (r && r.data) || {}
      const affected = data.affected != null ? data.affected : this.selected.length
      const skipped = (data.skipped_ids && data.skipped_ids.length) ? `, ${data.skipped_ids.length} skipped` : ''
      this.$showSuccess(`Done: ${affected} affected${skipped}`)
      this.loadData()
    },
    onBulkStatus() {
      if (!this.bulkStatus || !this.selected.length) {
        return
      }
      this.isBulking = true
      const scope = this.selectedScope()
      API.call('/cms/item/bulk-status.json', {
        item_ids: scope.item_ids,
        entity_ids: scope.entity_ids,
        status: this.bulkStatus,
      }).then(r => {
        this.statusDialog = false
        this.bulkStatus = null
        this.reportBulk(r)
      }).catch(() => {
        this.$showError('Bulk status update failed')
      }).finally(() => {
        this.isBulking = false
      })
    },
    onBulkDelete() {
      if (!this.selected.length) {
        return
      }
      this.$root.$confirm('Delete items', `Delete ${this.selected.length} selected item(s)? This cannot be undone.`).then(confirmed => {
        if (!confirmed) {
          return
        }
        this.isBulking = true
        const scope = this.selectedScope()
        API.call('/cms/item/bulk-delete.json', {
          item_ids: scope.item_ids,
          entity_ids: scope.entity_ids,
        }).then(r => {
          this.reportBulk(r)
        }).catch(() => {
          this.$showError('Bulk delete failed')
        }).finally(() => {
          this.isBulking = false
        })
      })
    },
  },
}
</script>